import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logoImage from '../assets/images/STS_Logo.png';
import { apiUrl } from '../api';
import { getDefaultDashboardRoute, normalizeRole } from './manageUsers.utils';
import { getOrCreateLoginDeviceId } from './session.utils';
import { validateEmail, validatePassword, validateOtp, WEBSITE_PASSWORD_MIN_LENGTH } from '../utils/validation.utils';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please login and verify OTP again.';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingChallengeId, setPendingChallengeId] = useState(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpNotice, setOtpNotice] = useState('');
  const [rememberToken, setRememberToken] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [skipOtpFor30Days, setSkipOtpFor30Days] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Field validation states
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpTouched, setOtpTouched] = useState(false);

  // Refs for focus management
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const authRequestInFlightRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Validation handlers
  const validateEmailField = (emailValue) => {
    const validation = validateEmail(emailValue);
    const error = !String(emailValue || '').trim()
      ? 'Please fill out this field.'
      : (validation.error || '');
    setEmailError(error);
    return validation.isValid;
  };

  const validatePasswordField = (passwordValue) => {
    const validation = validatePassword(passwordValue);
    if (!String(passwordValue || '').trim()) {
      setPasswordError('Please fill out this field.');
      return false;
    }
    if (!validation.isValid) {
      setPasswordError(validation.error || '');
      return false;
    }

    if (passwordValue.length < WEBSITE_PASSWORD_MIN_LENGTH) {
      setPasswordError(`Password must be at least ${WEBSITE_PASSWORD_MIN_LENGTH} characters.`);
      return false;
    }

    setPasswordError('');
    return true;
  };

  const validateOtpField = (otpValue) => {
    const validation = validateOtp(otpValue);
    const error = !String(otpValue || '').trim()
      ? 'Please fill out this field.'
      : (validation.error || '');
    setOtpError(error);
    return validation.isValid;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // Keep validation inline and responsive while the user types.
    // Empty initial state stays quiet until touched, but any non-empty value is validated immediately.
    if (emailTouched || emailError || value.length > 0) {
      validateEmailField(value);
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    validateEmailField(email);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    // Keep validation inline and responsive while the user types.
    // Empty initial state stays quiet until touched, but any non-empty value is validated immediately.
    if (passwordTouched || passwordError || value.length > 0) {
      validatePasswordField(value);
    }
  };

  const handlePasswordBlur = () => {
    setPasswordTouched(true);
    validatePasswordField(password);
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ''); // Only allow digits
    setOtp(value);
    setErrorMessage('');

    // Keep an expired OTP error visible until a new code is issued.
    if (otpExpiresAt && countdown <= 0) {
      setOtpError('OTP expired. Please resend a new verification code.');
      return;
    }

    // Validate format immediately after the field is active or once an error is present.
    if (otpTouched || otpError || value.length > 0) {
      validateOtpField(value);
    }
  };

  const handleOtpBlur = () => {
    setOtpTouched(true);
    validateOtpField(otp);
  };

  // Load saved theme on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);
    const savedToken = localStorage.getItem('rememberToken');
    if (savedToken) setRememberToken(savedToken);
    setDeviceId(getOrCreateLoginDeviceId());
  }, []);

  useEffect(() => {
    if (location?.state?.sessionExpired) {
      setErrorMessage(SESSION_EXPIRED_MESSAGE);
    }
  }, [location?.state]);

  useEffect(() => {
    if (!otpExpiresAt) {
      setCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expires = new Date(otpExpiresAt).getTime();
      const remaining = Math.max(0, Math.round((expires - now) / 1000));
      setCountdown(remaining);

      if (remaining <= 0) {
        setOtpError('OTP expired. Please resend a new verification code.');
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [otpExpiresAt]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const interval = setInterval(() => {
      setResendCooldown((previous) => Math.max(0, previous - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const persistSuccessfulLogin = (payload) => {
    const requiresInitialPasswordSetup = payload?.requiresInitialPasswordSetup === true
      || payload?.user?.requiresInitialPasswordSetup === true;
    const sessionUser = {
      ...payload.user,
      mustChangePassword: requiresInitialPasswordSetup,
      requiresInitialPasswordSetup,
    };

    if (payload.rememberToken) {
      localStorage.setItem('rememberToken', payload.rememberToken);
      setRememberToken(payload.rememberToken);
    }

    localStorage.setItem('loggedInUser', JSON.stringify(sessionUser));
    const role = normalizeRole(sessionUser.role);
    navigate(getDefaultDashboardRoute(role), {
      state: { loginStatus: { message: `Welcome back, ${sessionUser.name}!`, type: 'success' } },
    });
  };

  const handleLogin = async () => {
    if (authRequestInFlightRef.current) return;
    // Mark all fields as touched
    setEmailTouched(true);
    setPasswordTouched(true);

    // Validate all fields
    const isEmailValid = validateEmailField(email);
    const isPasswordValid = validatePasswordField(password);

    if (!isEmailValid || !isPasswordValid) {
      // Focus first invalid field
      if (!isEmailValid && emailInputRef.current) {
        emailInputRef.current.focus();
      } else if (!isPasswordValid && passwordInputRef.current) {
        passwordInputRef.current.focus();
      }
      // Clear any backend error message when validation fails
      setErrorMessage('');
      return;
    }

    authRequestInFlightRef.current = true;
    setErrorMessage('');
    setOtpNotice('');
    setLoading(true);
    try {
      const response = await fetch(apiUrl('/api/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email.trim().toLowerCase(),
          password: password,
          rememberToken: rememberToken || undefined,
          deviceId,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const backendError = data.error || 'Invalid email or password.';
        const normalizedError = String(backendError).toLowerCase();

        if (response.status === 401 && normalizedError.includes('incorrect password')) {
          setPasswordTouched(true);
          setPasswordError('Incorrect password.');
          setErrorMessage('');
          if (passwordInputRef.current) passwordInputRef.current.focus();
          return;
        }

        if (response.status === 404 && normalizedError.includes('email not found')) {
          setEmailTouched(true);
          setEmailError('Email not found.');
          setErrorMessage('');
          if (emailInputRef.current) emailInputRef.current.focus();
          return;
        }

        if (normalizedError.includes('temporary password expired')) {
          setPasswordTouched(true);
          setPasswordError(backendError);
          setErrorMessage('');
          if (passwordInputRef.current) passwordInputRef.current.focus();
          return;
        }

        setErrorMessage(backendError);
        return;
      }

      if (data.success && data.user) {
        persistSuccessfulLogin(data);
        return;
      }

      if (data.step === 2) {
        setPendingUserId(data.userId);
        setPendingChallengeId(data.challengeId || null);
        setOtpExpiresAt(data.otpExpiresAt);
        setResendCooldown(Math.max(0, Math.ceil((new Date(data.resendAvailableAt || Date.now()).getTime() - Date.now()) / 1000)));
        setOtpNotice('');
        setStep(2);
        // Clear field errors when transitioning to OTP step
        setOtpError('');
        setOtpTouched(false);
        if (data.warning) {
          setErrorMessage(data.warning);
        } else {
          setOtpNotice('Verification code sent to your email.');
        }
      }
    } catch (error) {
      setErrorMessage('Network error. Please check if server is running.');
    } finally {
      authRequestInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (authRequestInFlightRef.current) return;
    // Mark OTP field as touched
    setOtpTouched(true);

    // Expired OTP is a field validation error and should block verification before the API call.
    if (otpExpiresAt && countdown <= 0) {
      setOtpError('OTP expired. Please resend a new verification code.');
      if (otpInputRef.current) {
        otpInputRef.current.focus();
      }
      setErrorMessage('');
      return;
    }

    // Validate OTP
    const isOtpValid = validateOtpField(otp);

    if (!isOtpValid) {
      // Focus OTP field
      if (otpInputRef.current) {
        otpInputRef.current.focus();
      }
      // Clear any backend error message when validation fails
      setErrorMessage('');
      return;
    }

    authRequestInFlightRef.current = true;
    setErrorMessage('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/login/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: pendingUserId,
          challengeId: pendingChallengeId || undefined,
          otp,
          deviceId,
          skipOtpFor30Days,
        }),
      });
      const result = await res.json();
      if (!res.ok) {
        const backendError = result.error || 'Invalid OTP.';
        const normalizedError = String(backendError).toLowerCase();

        setOtpTouched(true);

        if (normalizedError.includes('attempt limit') || normalizedError.includes('too many incorrect otp')) {
          const waitSeconds = Number(result.retryAfterSeconds) || 180;
          setOtpError('Too many incorrect OTP attempts. Please wait before requesting a new code.');
          setResendCooldown(waitSeconds);
          setOtpNotice('Too many incorrect OTP attempts. Please wait 3 minutes before requesting a new code.');
          setErrorMessage('');
          if (otpInputRef.current) otpInputRef.current.focus();
          return;
        }

        if (normalizedError === 'otp expired') {
          setOtpError('OTP expired. Please resend a new verification code.');
          setErrorMessage('');
          if (otpInputRef.current) otpInputRef.current.focus();
          return;
        }

        if (
          normalizedError.includes('incorrect otp')
          || normalizedError === 'invalid otp'
          || normalizedError.includes('invalid or expired otp')
        ) {
          setOtpError('Incorrect OTP. Please enter the latest verification code.');
          setErrorMessage('');
          if (otpInputRef.current) otpInputRef.current.focus();
          return;
        }

        setErrorMessage(backendError);
        return;
      }
      if (result.success && result.user) {
        persistSuccessfulLogin(result);
      }
    } catch (err) {
      setErrorMessage('Network error while verifying OTP.');
    } finally {
      authRequestInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (authRequestInFlightRef.current) return;
    if (!pendingUserId && !email) {
      setErrorMessage('Cannot resend OTP without a login attempt.');
      return;
    }
    if (resendCooldown > 0) {
      setOtpNotice(`Please wait ${resendCooldown}s before requesting another verification code.`);
      return;
    }
    authRequestInFlightRef.current = true;
    setErrorMessage('');
    setOtpNotice('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/login/resend-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pendingUserId, email: email.trim().toLowerCase(), challengeId: pendingChallengeId || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.code === 'OTP_ATTEMPT_COOLDOWN' || String(data.error || '').toLowerCase().includes('too many incorrect otp')) {
          const waitSeconds = Number(data.retryAfterSeconds) || 180;
          setResendCooldown(waitSeconds);
          setOtpNotice(`Too many incorrect OTP attempts. Please wait ${Math.ceil(waitSeconds / 60)} minutes before requesting a new code.`);
          setErrorMessage('');
          return;
        }

        if (Number(data.retryAfterSeconds) > 0) {
          setResendCooldown(Number(data.retryAfterSeconds));
          setOtpNotice(`Please wait ${data.retryAfterSeconds}s before requesting another verification code.`);
          setErrorMessage('');
          return;
        }

        setErrorMessage(data.error || 'Failed to resend OTP.');
        return;
      }
      setPendingChallengeId(data.challengeId || null);
      setOtp('');
      setOtpTouched(false);
      setOtpError('');
      setOtpExpiresAt(data.otpExpiresAt);
      setResendCooldown(Math.max(0, Math.ceil((new Date(data.resendAvailableAt || Date.now()).getTime() - Date.now()) / 1000)));
      setOtpNotice(data.warning || 'A new verification code was sent to your email.');
    } catch (err) {
      setErrorMessage('Network error while resending OTP.');
    } finally {
      authRequestInFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="sts-header-bar">
        <img src={logoImage} alt="STS Logo" className="sts-logo-circle" />
        <h2 className="sts-header-text">Login</h2>
      </div>

      <div className="login-flex-container">
        <div className="login-card-main">
          <button className="back-home-link" onClick={() => navigate('/')}>← Back to Home</button>
          
          <h3 className="login-title-sts">
            {step === 1 ? 'Enter your Credentials' : 'Security Verification'}
          </h3>

          {step === 1 ? (
            <>

              <div className="sts-input-group">
                <label className="sts-label" htmlFor="email-input">Email Address</label>
                <input
                  id="email-input"
                  ref={emailInputRef}
                  type="email"
                  className={`sts-input-field ${emailTouched && emailError ? 'sts-input-error' : ''}`}
                  placeholder="email@example.com"
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  disabled={loading}
                  aria-invalid={emailTouched && !!emailError}
                  aria-describedby={emailError ? 'email-error' : undefined}
                />
                {emailTouched && emailError && (
                  <div id="email-error" className="sts-field-error">
                    {emailError}
                  </div>
                )}
              </div>

              <div className="sts-input-group">
                <label className="sts-label" htmlFor="password-input">Password</label>
                
                <div className="password-field-wrapper login-password-field">
                  <input
                    id="password-input"
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    className={`sts-input-field ${passwordTouched && passwordError ? 'sts-input-error' : ''}`}
                    placeholder="••••••••"
                    value={password}
                    onChange={handlePasswordChange}
                    onBlur={handlePasswordBlur}
                    disabled={loading}
                    aria-invalid={passwordTouched && !!passwordError}
                    aria-describedby={passwordError ? 'password-error' : undefined}
                  />

                  <button
                    type="button"
                    className="password-toggle-button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    )}
                  </button>
                </div>

                {passwordTouched && passwordError && (
                  <div id="password-error" className="sts-field-error">
                    {passwordError}
                  </div>
                )}

                <div className="pw-footer-row">
                  <span className="forgot-pass-link-bottom" onClick={() => navigate('/reset-password')}>
                    Forgot Password?
                  </span>
                </div>
              </div>

              <button className="sts-login-button" onClick={handleLogin} disabled={loading}>
                {loading ? 'LOGGING IN...' : 'LOGIN'}
              </button>
            </>
          ) : (
            <div style={{ marginTop: 16 }}>
              <p className="verification-instruction" style={{ textAlign: 'center', fontSize: '14px', marginBottom: '15px' }}>
                Enter the 6-digit code sent to <b>{email}</b>
              </p>
              <div className="sts-input-group">
                <label className="sts-label" htmlFor="otp-input">Verification Code</label>
                <input
                  id="otp-input"
                  ref={otpInputRef}
                  className={`sts-input-field otp-input ${otpTouched && otpError ? 'sts-input-error' : ''}`}
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={handleOtpChange}
                  onBlur={handleOtpBlur}
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  aria-invalid={otpTouched && !!otpError}
                  aria-describedby={otpError ? 'otp-error' : undefined}
                />
                {otpTouched && otpError && (
                  <div id="otp-error" className="sts-field-error">
                    {otpError}
                  </div>
                )}
              </div>
              <label className="otp-device-skip-option">
                <input
                  type="checkbox"
                  checked={skipOtpFor30Days}
                  onChange={(event) => setSkipOtpFor30Days(event.target.checked)}
                  disabled={loading}
                />
                <span>Trust this device for 30 days</span>
              </label>
              {otpNotice && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    marginBottom: '12px',
                    padding: '10px 14px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    backgroundColor: document.documentElement.getAttribute('data-theme') === 'dark' ? '#1f3b2b' : '#e8f5e9',
                    color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#b7f0c2' : '#1b5e20',
                    border: document.documentElement.getAttribute('data-theme') === 'dark' ? '1px solid #356348' : '1px solid #b7dfba'
                  }}
                >
                  {otpNotice}
                </div>
              )}

              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <small style={{ color: '#555' }}>
                    {countdown > 0 ? `Code expires in ${countdown}s` : 'Code expired.'}
                  </small>
                  <small style={{ color: '#555' }}>
                    {resendCooldown > 0 ? `Resend available in ${resendCooldown}s` : 'You can request a new code'}
                  </small>
                </div>
                <button
                  className="resend-otp-button"
                  type="button"
                  onClick={handleResendOtp}
                  disabled={loading || resendCooldown > 0}
                  style={{ padding: '8px 16px', borderRadius: '6px', cursor: loading || resendCooldown > 0 ? 'not-allowed' : 'pointer', width: '100%' }}
                >
                  {resendCooldown > 0 ? `RESEND CODE (${resendCooldown}s)` : 'RESEND CODE'}
                </button>
              </div>
              <button className="sts-login-button" onClick={handleVerifyOtp} disabled={loading}>
                {loading ? 'VERIFYING...' : 'VERIFY CODE'}
              </button>
              <button
                onClick={() => {
                  setStep(1);
                  setPendingUserId(null);
                  setPendingChallengeId(null);
                  setOtp('');
                  setOtpExpiresAt(null);
                  setResendCooldown(0);
                  setOtpNotice('');
                  setSkipOtpFor30Days(false);
                  setErrorMessage('');
                  // Clear field errors
                  setOtpError('');
                  setOtpTouched(false);
                  setEmailTouched(false);
                  setPasswordTouched(false);
                }}
                className="back-login-btn"
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>

      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: document.documentElement.getAttribute('data-theme') === 'dark' ? '#5a2a2a' : '#f8d7da',
          color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#ffb3b3' : '#721c24',
          padding: '12px 20px',
          borderRadius: '4px',
          border: document.documentElement.getAttribute('data-theme') === 'dark' ? '1px solid #8b3d3d' : '1px solid #f5c6cb',
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          {errorMessage}
          <button 
            onClick={() => setErrorMessage('')}
            style={{
              marginLeft: '10px',
              background: 'none',
              border: 'none',
              color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#ffb3b3' : '#721c24',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
